import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'antd';
import {
  arraySort,
  canBeEditable,
  momentizeObjectDates,
} from '@core/utils/methods';
import {
  JURISDICTION_DATES_FOR_MOMENTIZE,
  YES_NO_GROUP,
} from '@models/constants';
import { JurisdictionDetailsZoning } from '@models/interfaces';
import {
  Autocomplete,
  DatePicker,
  Input,
  InputNumber,
  RadioGroup,
  ShowMoreInput,
} from '@shared/modules';
import { DDVSelectors } from '@store/selectors';
import { ZoningNSBFormProps } from '../../models';

export const ZoningNSBForm: FC<ZoningNSBFormProps> = ({
  isEditing,
  permissions: {
    applicationRequirement: applicationRequirementPermissions,
    describeOverallProcess: describeOverallProcessPermissions,
    estimatedZoningApprovalTimeline,
    otherFee,
    specialLicensingOrBondingCondition:
      specialLicensingOrBondingConditionPermissions,
    thirdPartyCostRequired,
    isWrittenDocumentationFromJurisdiction,
    jurisdictionZoningNote: jurisdictionZoningNotePermissions,
    zoningRequired,
    separationRequirement,
    setback: setbackPermissions,
    towerHeightRestriction,
    jurisdictionReviewDate,
  },
  formValue: {
    jurisdictionZoningNote,
    applicationRequirement,
    describeOverallProcess,
    setback,
    specialLicensingOrBondingCondition,
    ...formValue
  },
  formUpdating,
}: PropsWithChildren<ZoningNSBFormProps>) => {
  const [form] = Form.useForm();

  const { ZoningRequiredDDV, EstimatedZoningApprovalTimelineDDV } = useSelector(
    DDVSelectors.getDDVValuesAsOptionsByTypes([
      'ZoningRequired',
      'EstimatedZoningApprovalTimeline',
    ]),
  );

  useEffect(() => {
    form.setFieldsValue(
      momentizeObjectDates(
        formValue as JurisdictionDetailsZoning,
        JURISDICTION_DATES_FOR_MOMENTIZE,
      ),
    );
  }, [isEditing]);

  const update = (): void => {
    if (formUpdating) {
      formUpdating(formValue.id, form.getFieldsValue());
    }
  };

  return (
    <Form
      className="prov-jurisdiction-configurable-panel__form"
      labelCol={{ span: 10 }}
      form={form}
      onValuesChange={update}
    >
      <Autocomplete
        id="zoningRequired"
        className="left"
        label="Zoning Required"
        options={arraySort(ZoningRequiredDDV, 'DESC', 'viewValue')}
        isEditing={canBeEditable(zoningRequired, isEditing)}
        elementProps={{ allowClear: true }}
      />
      <ShowMoreInput
        id="setback"
        className="right"
        label="Setbacks"
        data={setback}
        form={form}
        isEditing={canBeEditable(setbackPermissions, isEditing)}
      />
      <RadioGroup
        id="isWrittenDocumentationFromJurisdiction"
        className="left"
        label="Written Documentation from Jurisdiction"
        isEditing={canBeEditable(
          isWrittenDocumentationFromJurisdiction,
          isEditing,
        )}
        buttons={YES_NO_GROUP}
      />
      <ShowMoreInput
        id="specialLicensingOrBondingCondition"
        className="right"
        label="Special Licensing/Bonding Conditions"
        data={specialLicensingOrBondingCondition}
        form={form}
        isEditing={canBeEditable(
          specialLicensingOrBondingConditionPermissions,
          isEditing,
        )}
      />
      <ShowMoreInput
        id="describeOverallProcess"
        className="left"
        label="Describe Overall Process"
        data={describeOverallProcess}
        form={form}
        isEditing={canBeEditable(describeOverallProcessPermissions, isEditing)}
      />
      <InputNumber
        id="thirdPartyCostRequired"
        className="right"
        label="Third-Party Cost Required"
        elementProps={{ min: 1 }}
        isEditing={canBeEditable(thirdPartyCostRequired, isEditing)}
      />
      <Autocomplete
        id="estimatedZoningApprovalTimeline"
        className="left"
        label="Estimated Zoning Approval Timeline"
        options={EstimatedZoningApprovalTimelineDDV}
        isEditing={canBeEditable(estimatedZoningApprovalTimeline, isEditing)}
        elementProps={{ allowClear: true }}
      />
      <InputNumber
        id="otherFee"
        className="right"
        label="Other Fees"
        elementProps={{ min: 1 }}
        isEditing={canBeEditable(otherFee, isEditing)}
      />
      <ShowMoreInput
        id="applicationRequirement"
        className="left"
        label="Application Requirements"
        data={applicationRequirement}
        form={form}
        isEditing={canBeEditable(applicationRequirementPermissions, isEditing)}
      />
      <ShowMoreInput
        id="jurisdictionZoningNote"
        className="right"
        data={jurisdictionZoningNote}
        form={form}
        label="Zoning Notes"
        isEditing={canBeEditable(jurisdictionZoningNotePermissions, isEditing)}
      />
      <InputNumber
        id="towerHeightRestriction"
        className="left"
        label="Tower Height Restrictions"
        elementProps={{ min: 1 }}
        isEditing={canBeEditable(towerHeightRestriction, isEditing)}
      />
      <DatePicker
        id="jurisdictionReviewDate"
        className="right"
        label="Jurisdiction Review Date"
        isEditing={canBeEditable(jurisdictionReviewDate, isEditing)}
      />
      <Input
        id="separationRequirement"
        className="left"
        label="Separation Requirements"
        isEditing={canBeEditable(separationRequirement, isEditing)}
      />
    </Form>
  );
};
